import React from 'react';

const DEFAULT_TYPE_SCALE_RANGE = {
  min: 0.5,
  max: Infinity,
  step: 0.05
};

export const DEFAULT_TYPE_SCALE = {
  title: 1.3,
  heading: 1.35,
  body: 1.4,
  price: 1.3,
  description: 1.7
};

export const TEMPLATES = [
  {
    id: 'artisan',
    label: 'Artisan Bakery',
    desc: 'Warm wheat cream with terracotta accents & handcrafted serif type',
    chip: 'Warm',
    palette: ['#fffcf7', '#5c3a21', '#c87d55']
  },
  {
    id: 'trattoria',
    label: 'Tuscan Trattoria',
    desc: 'Classic Italian warmth with olive green & wine crimson borders',
    chip: 'Bistro',
    palette: ['#faf7f0', '#7a2828', '#4a6042']
  },
  {
    id: 'champagne',
    label: 'Champagne Lounge',
    desc: 'Luxurious pearl white with delicate rose gold & champagne accents',
    chip: 'Luxury',
    palette: ['#ffffff', '#8a5d6a', '#d4af37']
  },
  {
    id: 'seaside',
    label: 'Seaside Cafe',
    desc: 'Airy ocean white with azure blue & coral sand accents',
    chip: 'Fresh',
    palette: ['#f4f9fc', '#1b4965', '#e28f6e']
  },
  {
    id: 'bistronomi',
    label: 'Bistronomi',
    desc: 'Soft blush cream with rich espresso & plum typography',
    chip: 'Chic',
    palette: ['#faf4f2', '#3d252a', '#9e5d6d']
  },
  {
    id: 'taverna',
    label: 'Greek Taverna',
    desc: 'Bright Mediterranean white with cobalt blue & lemon gold',
    chip: 'Coastal',
    palette: ['#ffffff', '#0f4c81', '#e5a93b']
  },
  {
    id: 'matcha',
    label: 'Matcha & Tea',
    desc: 'Soothing ceramic white with soft sage green & matcha accents',
    chip: 'Zen',
    palette: ['#f6f8f5', '#2e4a37', '#7fa083']
  },
  {
    id: 'nordic',
    label: 'Nordic Clean',
    desc: 'Ultra-minimal Scandinavian white with slate blue & birch wood',
    chip: 'Minimal',
    palette: ['#ffffff', '#2b3a4a', '#a89f91']
  },
  {
    id: 'sunset',
    label: 'Sunset Bistro',
    desc: 'Golden hour cream with coral amber & dusk violet accents',
    chip: 'Vibrant',
    palette: ['#fffaf5', '#7a3e5d', '#d97736']
  },
  {
    id: 'oysterbar',
    label: 'Oyster Bar & Grill',
    desc: 'Nautical pearl white with deep navy & brass gold trim',
    chip: 'Seafood',
    palette: ['#f8fafc', '#0f2038', '#c59b27']
  },
  {
    id: 'minimal',
    label: 'Minimal',
    desc: 'Quiet, upscale typography with crisp monochromatic lines',
    chip: 'Clean',
    palette: ['#ffffff', '#141414', '#888888']
  },
  {
    id: 'coastal',
    label: 'Coastal',
    desc: 'Airy seaside look with cool blue accents',
    chip: 'Fresh',
    palette: ['#f4f8f8', '#163d4d', '#8db7c5']
  },
  {
    id: 'editorial',
    label: 'Editorial',
    desc: 'Bold, fashion-led typography with modern spacing',
    chip: 'Modern',
    palette: ['#f3efe8', '#111111', '#d86f45']
  },
  {
    id: 'gardenparty',
    label: 'Garden Party',
    desc: 'Outdoor elegance with watercolor florals and sage tones',
    chip: 'Outdoor',
    palette: ['#f7f8f2', '#3d5a3c', '#d4b896']
  },
  {
    id: 'heritage',
    label: 'Heritage',
    desc: 'Country club elegance with framed details',
    chip: 'Formal',
    palette: ['#f7f1e6', '#22304a', '#b58b52']
  },
  {
    id: 'patisserie',
    label: 'Patisserie',
    desc: 'Soft boutique styling for desserts and brunch',
    chip: 'Soft',
    palette: ['#fff8f6', '#6e4b58', '#e0a6a0']
  },
  {
    id: 'riviera',
    label: 'Riviera',
    desc: 'Mediterranean coastline with warm terracotta and azure',
    chip: 'Coastal',
    palette: ['#fcf8f3', '#1a3a5c', '#e2956a']
  },
  {
    id: 'sunstone',
    label: 'Sunstone',
    desc: 'Warm hospitality look with terracotta energy',
    chip: 'Warm',
    palette: ['#fbf1e7', '#5a3428', '#db7b52']
  },
  {
    id: 'dessertatelier',
    label: 'Dessert Atelier',
    desc: 'White patisserie styling with berry and champagne accents',
    chip: 'Dessert',
    palette: ['#ffffff', '#65364f', '#d9a95f']
  },
  {
    id: 'liquor',
    label: 'Liquor',
    desc: 'Clean white spirits edition with warm amber details',
    chip: 'Spirits',
    palette: ['#ffffff', '#2c2119', '#b76f2c']
  }
];

const LAYOUTS = [
  { id: 'full', label: 'Full Page', desc: 'Menu fills the entire page' },
  { id: 'half', label: 'Half Page (2-up)', desc: 'Two copies side by side — cut to get 2 cards' },
];

const PAGES = [
  { id: 'front-only', label: 'Front Only', desc: 'Single side' },
  { id: 'front-back', label: 'Front + Back', desc: 'Main menu front, desserts back' },
];

const TYPE_CONTROLS = [
  { id: 'title', label: 'Title', desc: 'Main page title size' },
  { id: 'heading', label: 'Heading', desc: 'Category heading size' },
  { id: 'body', label: 'Body', desc: 'Item name size' },
  { id: 'price', label: 'Price', desc: 'Price text size' },
  {
    id: 'description',
    label: 'Description',
    desc: 'Description text only',
    min: 0.5,
    step: 0.1
  }
];

export default function ConfigPanel({ config, onChange, mealTypes, categories, hideMealTypeAndTemplate = false }) {
  const selectedTemplate = TEMPLATES.find(template => template.id === config.template) || TEMPLATES[0];
  const typeScale = { ...DEFAULT_TYPE_SCALE, ...(config.typeScale || {}) };
  const descriptionBold = config.descriptionBold !== false;

  const update = (key, value) => {
    onChange({ ...config, [key]: value });
  };

  const getScaleRange = (key) => {
    const control = TYPE_CONTROLS.find(item => item.id === key);
    return {
      min: control?.min ?? DEFAULT_TYPE_SCALE_RANGE.min,
      max: control?.max ?? DEFAULT_TYPE_SCALE_RANGE.max,
      step: control?.step ?? DEFAULT_TYPE_SCALE_RANGE.step
    };
  };

  const clampScale = (key, value) => {
    const { min, max } = getScaleRange(key);
    return Math.max(min, Math.min(max, value));
  };

  const updateTypeScale = (key, delta) => {
    const nextValue = clampScale(key, Math.round(((typeScale[key] || DEFAULT_TYPE_SCALE[key] || 1) + delta) * 100) / 100);
    update('typeScale', {
      ...typeScale,
      [key]: nextValue
    });
  };

  const resetTypeScale = (key) => {
    update('typeScale', {
      ...typeScale,
      [key]: DEFAULT_TYPE_SCALE[key] || 1
    });
  };

  const toggleBackCategory = (catName) => {
    const current = config.backCategories || [];
    if (current.includes(catName)) {
      update('backCategories', current.filter(c => c !== catName));
    } else {
      update('backCategories', [...current, catName]);
    }
  };

  return (
    <div className="config-panel">
      {!hideMealTypeAndTemplate && (
        <>
          {/* Meal Type */}
          <div className="config-section">
            <label className="config-label">Meal Type</label>
            <select
              className="config-select"
              value={mealTypes.includes(config.mealType) ? config.mealType : 'custom'}
              onChange={e => {
                const val = e.target.value;
                if (val === 'custom') {
                  update('mealType', '');
                } else {
                  update('mealType', val);
                }
              }}
            >
              {mealTypes.map(mt => (
                <option key={mt} value={mt}>{mt}</option>
              ))}
              <option value="custom">Custom...</option>
            </select>
            {(!mealTypes.includes(config.mealType) || config.mealType === '') && (
              <input
                type="text"
                className="venue-input animate-in"
                value={config.mealType}
                onChange={e => update('mealType', e.target.value)}
                placeholder="e.g. Summer Specials, Late Night Bar"
                maxLength={32}
                style={{ marginTop: '0.45rem' }}
              />
            )}
          </div>

          {/* Template */}
          <div className="config-section">
            <label className="config-label">Template Style</label>
            <select
              className="config-select"
              value={config.template}
              onChange={e => update('template', e.target.value)}
            >
              {TEMPLATES.map(template => (
                <option key={template.id} value={template.id}>{template.label}</option>
              ))}
            </select>
            <div className="template-select-summary">
              <div className="template-select-row">
                <span className="template-chip">{selectedTemplate.chip}</span>
                <span className="template-swatches" aria-hidden="true">
                  {selectedTemplate.palette.map(color => (
                    <span
                      key={color}
                      className="template-swatch"
                      style={{ background: color }}
                    />
                  ))}
                </span>
              </div>
              <p className="config-hint">{selectedTemplate.desc}</p>
            </div>
          </div>
        </>
      )}

      <div className="config-section">
        <label className="config-label">Typography</label>
        <div className="type-control-list">
          {TYPE_CONTROLS.map(control => {
            const value = typeScale[control.id] || 1;
            const { min, max, step } = getScaleRange(control.id);
            const isMin = value <= min;
            const isMax = value >= max;

            return (
              <div key={control.id} className="type-control-card">
                <div className="type-control-copy">
                  <strong>{control.label}</strong>
                  <small>{control.desc}</small>
                </div>
                <div className="type-control-actions">
                  <button
                    type="button"
                    className="type-size-btn"
                    onClick={() => updateTypeScale(control.id, -step)}
                    disabled={isMin}
                    aria-label={`Decrease ${control.label} size`}
                  >
                    -
                  </button>
                  <button
                    type="button"
                    className={`type-size-btn type-size-value ${Math.abs(value - (DEFAULT_TYPE_SCALE[control.id] || 1)) < 0.001 ? 'is-default' : ''}`}
                    onClick={() => resetTypeScale(control.id)}
                    aria-label={`Reset ${control.label} size`}
                  >
                    {Math.round(value * 100)}%
                  </button>
                  <button
                    type="button"
                    className="type-size-btn"
                    onClick={() => updateTypeScale(control.id, step)}
                    disabled={isMax}
                    aria-label={`Increase ${control.label} size`}
                  >
                    +
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <label className="type-toggle">
          <input
            type="checkbox"
            checked={descriptionBold}
            onChange={e => update('descriptionBold', e.target.checked)}
          />
          <span>Bold descriptions</span>
        </label>
        <p className="config-hint">Tap the percentage to reset that text group back to default.</p>
      </div>

      {/* Layout */}
      <div className="config-section">
        <label className="config-label">Layout</label>
          <div className="config-options layout-options">
            {LAYOUTS.map(l => (
              <button
                key={l.id}
                type="button"
                className={`config-option-btn ${config.layout === l.id ? 'active' : ''}`}
                onClick={() => update('layout', l.id)}
              >
                <strong>{l.label}</strong>
              <small>{l.desc}</small>
            </button>
          ))}
        </div>
      </div>

      {/* Pages */}
      <div className="config-section">
        <label className="config-label">Pages</label>
          <div className="config-options page-options">
            {PAGES.map(p => (
              <button
                key={p.id}
                type="button"
                className={`config-option-btn ${config.pages === p.id ? 'active' : ''}`}
                onClick={() => update('pages', p.id)}
              >
                <strong>{p.label}</strong>
              <small>{p.desc}</small>
            </button>
          ))}
        </div>
      </div>

      {/* Back page category assignment */}
      {config.pages === 'front-back' && categories.length > 0 && (
        <div className="config-section">
          <label className="config-label">Which categories go on the back?</label>
          <div className="back-cat-options">
            {categories.map(catName => (
              <label key={catName} className="back-cat-toggle">
                <input
                  type="checkbox"
                  checked={(config.backCategories || []).includes(catName)}
                  onChange={() => toggleBackCategory(catName)}
                />
                <span>{catName}</span>
              </label>
            ))}
          </div>
          <p className="config-hint">
            Unchecked categories appear on the front page.
          </p>
        </div>
      )}
    </div>
  );
}
